import { useEffect, useState, useRef } from "react";
import WebsiteShowcaseSection from "../sections/WebsiteShowcaseSection";
import { BRAND_CONFIGS, type BrandType } from "../../../config/brandConfig";
type TemplateKey = "design" | "it" | "short";
const TEMPLATES = {
  design: {
    heroSkill: "Design Expert",
    heroSkillSub: "Adobe · Figma",
    heroSkillIcon: "🎨",
    heroSub:
      "Your brand deserves to look as ambitious as the vision behind it. Here's how I make that happen — for businesses exactly like yours.",
storyHeadline: (
  <>
    I didn't start as a <em>designer.</em> I started as a problem solver.
  </>
),
    timeline: [
      {
        icon: "💡",
        year: "The Realization",
        title: "Businesses were failing visually",
        desc: "Great products, weak brands. I watched startups lose clients not because their work was bad — but because their visual identity couldn't communicate it.",
      },
      {
        icon: "🎨",
        year: "The Commitment",
        title: "Mastering the craft",
        desc: "I went deep — Illustrator, Photoshop, Figma. Not just tools, but the thinking behind visual communication that actually converts.",
      },
      {
        icon: "🚀",
        year: "Today",
        title: "Top Rated. 100% JSS.",
        desc: "Every project I take carries the same mission — make your business look exactly as good as the vision behind it.",
      },
    ],
    storyRightPill: "Philosophy",
    pullquote:
      "Your first impression isn't your handshake — it's your visual identity. And most businesses are leaving money on the table because theirs isn't working hard enough.",
    pullquoteAttr: "— Irfan Baig",
    storyBody: [
      <>
        I've spent years working exclusively with{" "}
        <strong>Startups and SMEs</strong> — businesses in that critical phase
        where how you look determines whether clients trust you enough to even
        have a conversation.
      </>,
      <>
        My approach is consultative, not transactional. I ask questions most
        designers skip — about your audience, your competition, the emotion you
        want your brand to evoke. Then I translate that into design that doesn't
        just look good, it <strong>works</strong> for your business goals.
      </>,
    ],
    credChips: [
      { icon: "⭐", text: "Top Rated" },
      { icon: "✅", text: "100% Job Success" },
      { icon: "🎨", text: "Adobe · Figma" },
    ],
    processTitle: (
      <>
        The Design <em>Process</em>
      </>
    ),
    processSub:
      "A structured, repeatable approach that removes guesswork — so you always know what's happening and why.",
    steps: [
      {
        icon: "🔍",
        title: "Discovery",
        tag: "Step 01",
        desc: "Deep questions about your brand, audience, and goals — before any tool is opened. Strategy first, always.",
      },
      {
        icon: "✏️",
        title: "Concept",
        tag: "Step 02",
        desc: "Initial creative directions delivered fast — so you see options early and shape the outcome from day one.",
      },
      {
        icon: "🔄",
        title: "Refinement",
        tag: "Step 03",
        desc: "Collaborative iteration on your feedback until every detail feels exactly right. No guessing, no assumptions.",
      },
      {
        icon: "📦",
        title: "Delivery",
        tag: "Step 04",
        desc: "Final files in every format you need — AI, PNG, PDF, SVG — organized, named, and ready to deploy.",
      },
    ],
    offerTitle: (
      <>
        Here's exactly what I'd
        <br />
        do for <em>this job</em>
      </>
    ),
    showOfferGrid: true,
    tools: [
      { icon: "🎨", name: "Adobe Illustrator" },
      { icon: "🖼", name: "Photoshop" },
      { icon: "📐", name: "Figma" },
    ],
    portfolio: [
      {
        icon: "🎯",
        tag: "Branding",
        name: "Brand Identity System",
        desc: "Replace with your real project name & 1-line result",
      },
      {
        icon: "📱",
        tag: "Social Media",
        name: "Social Media Suite",
        desc: "Replace with your real project name & 1-line result",
      },
      {
        icon: "🖥",
        tag: "UI Design",
        name: "Product UI Design",
        desc: "Replace with your real project name & 1-line result",
      },
    ],
    ctaTitle: (
      <>
        Ready to make your brand look
        <br />
        as good as <em>your vision?</em>
      </>
    ),
    ctaBody:
      "I don't hard sell — I let the work speak. But if what you've read resonates, I'd love a quick 15-minute call to understand your project better before we even discuss budget. No pressure. Just a smart first conversation.",
  },
  it: {
    heroSkill: "IT & Dev Expert",
    heroSkillSub: "Full Stack",
    heroSkillIcon: "💻",
    heroSub:
      "Great software isn't just built — it's thought through. Let me show you how I approach your project, from first line to final delivery.",
storyHeadline: (
  <>
    I didn't start as a <em>designer.</em> I started as a problem solver.
  </>
),
    timeline: [
      {
        icon: "🔍",
        year: "The Pattern I Noticed",
        title: "Code without questions fails",
        desc: "I watched teams spend months building products that collapsed under real users — not bad code, but no one asked the right questions before writing it.",
      },
      {
        icon: "⚙️",
        year: "The Method",
        title: "Business logic first, then code",
        desc: 'I became obsessive about understanding the "why" before the "how." Business outcomes drive every technical decision I make.',
      },
      {
        icon: "✅",
        year: "Today",
        title: "Top Rated. 100% JSS.",
        desc: "Clean code, clear communication, delivered on time. Every single time.",
      },
    ],
    storyRightPill: "Philosophy",
    pullquote:
      "The best code I've ever written came after the best conversation I ever had with a client. Understanding the problem completely is 60% of solving it.",
    pullquoteAttr: "— Irfan Baig",
    storyBody: [
      <>
        I'm not the cheapest developer on Upwork. I'm the one who{" "}
        <strong>delivers what was promised</strong>, communicates proactively,
        and leaves you with clean, documented work you actually own and
        understand.
      </>,
      <>
        I combine technical depth with a design-thinking mindset — which means
        the interfaces I build feel intuitive, and the systems I architect are
        built for the business logic behind them, not just the spec sheet.
      </>,
    ],
    credChips: [
      { icon: "⭐", text: "Top Rated" },
      { icon: "✅", text: "100% Job Success" },
      { icon: "💻", text: "Full Stack" },
    ],
    processTitle: (
      <>
        The Development <em>Process</em>
      </>
    ),
    processSub:
      "Every project follows a clear structure — so you always know where we are and what's coming next.",
    steps: [
      {
        icon: "💬",
        title: "Scoping Call",
        tag: "Step 01",
        desc: "15 minutes to align on requirements, edge cases, and success criteria — before writing a single line of code.",
      },
      {
        icon: "🏗️",
        title: "Architecture",
        tag: "Step 02",
        desc: "A clear build plan shared with you before work begins. Your input shapes the structure from the start.",
      },
      {
        icon: "⚙️",
        title: "Development",
        tag: "Step 03",
        desc: "Regular updates throughout. Clean, well-commented code. No disappearing into a black hole.",
      },
      {
        icon: "🚀",
        title: "Handover",
        tag: "Step 04",
        desc: "Documented, tested, production-ready. Handed over properly — not just uploaded and abandoned.",
      },
    ],
    offerTitle: (
      <>
        My plan for
        <br />
        <em>your specific</em> project
      </>
    ),
    showOfferGrid: true,
    tools: [
      { icon: "⚛️", name: "React / Vue" },
      { icon: "🐘", name: "Laravel / PHP" },
      { icon: "🐍", name: "Python" },
      { icon: "🌐", name: "WordPress" },
    ],
    portfolio: [
      {
        icon: "🛒",
        tag: "E-Commerce",
        name: "E-Commerce Platform",
        desc: "Replace with your real project name & 1-line result",
      },
      {
        icon: "📊",
        tag: "Dashboard",
        name: "Analytics Dashboard",
        desc: "Replace with your real project name & 1-line result",
      },
      {
        icon: "🔌",
        tag: "API / Backend",
        name: "REST API Build",
        desc: "Replace with your real project name & 1-line result",
      },
    ],
    ctaTitle: (
      <>
        Let's talk before
        <br />
        you <em>make a decision.</em>
      </>
    ),
    ctaBody:
      "A 15-minute conversation helps me give you a sharper proposal — and helps you see whether I'm the right fit. I'd rather earn your confidence with a conversation than win a contract through a polished pitch.",
  },
  short: {
    heroSkill: "Design + IT Expert",
    heroSkillSub: "Adobe · Figma",
    heroSkillIcon: "🎨",
    heroSub:
      "You're probably reading 30 proposals right now. I'll make this worth your next 90 seconds.",
    storyHeadline: (
      <>
        Here's what you
        <br />
        <em>actually</em> need
        <br />
        to know
        <br />
        about me.
      </>
    ),
    timeline: [
      {
        icon: "📋",
        year: "What I Do",
        title: "Design + IT for Startups & SMEs",
        desc: "Full-spectrum: brand identity, multimedia, web development. One person, one point of contact, complete delivery.",
      },
      {
        icon: "⭐",
        year: "My Track Record",
        title: "Top Rated · 100% Job Success",
        desc: "Not inflated numbers. Every job closed properly. Every client left satisfied.",
      },
      {
        icon: "🎯",
        year: "For This Job",
        title: "I read your brief. Really.",
        desc: "See my specific plan for your project on the right — no generic copy-paste.",
      },
    ],
    storyRightPill: "For Your Project",
    pullquote: "Best work delivered in short time.",
    pullquoteAttr: "— Previous Client, Startup Founder",
    storyBody: [
      <>
        I don't pad proposals. I read your brief carefully, I ask one smart
        question if needed, and I deliver <strong>exactly what was agreed</strong>{" "}
        — usually faster than expected.
      </>,
    ],
    storyPlaceholders: [
      "Paste AI-generated project-specific approach here",
      "One smart question about their brief goes here",
    ],
    credChips: [
      { icon: "⭐", text: "Top Rated" },
      { icon: "✅", text: "100% JSS" },
      { icon: "⚡", text: "Fast Delivery" },
    ],
    processTitle: (
      <>
        Clean. Fast. <em>Reliable.</em>
      </>
    ),
    processSub:
      "My process is lean by design — built to move fast without sacrificing quality.",
    steps: [
      {
        icon: "📋",
        title: "Read Brief",
        tag: "Step 01",
        desc: "I actually read it — fully — and note every detail that matters before doing anything else.",
      },
      {
        icon: "❓",
        title: "Smart Questions",
        tag: "Step 02",
        desc: "One or two targeted questions before starting. No guessing, no wasted effort on either side.",
      },
      {
        icon: "⚡",
        title: "Fast Execution",
        tag: "Step 03",
        desc: "Delivered quickly. On time. No excuses, no disappearing, no surprises.",
      },
      {
        icon: "✅",
        title: "Done Right",
        tag: "Step 04",
        desc: "Revisions if needed. Handover clean. Every job closed as well as it was started.",
      },
    ],
    offerTitle: null,
    showOfferGrid: false,
    tools: [
      { icon: "🎨", name: "Adobe Suite" },
      { icon: "📐", name: "Figma" },
      { icon: "💻", name: "Web Dev" },
    ],
    portfolio: [
      {
        icon: "⭐",
        tag: "Best Match",
        name: "Most Relevant Project",
        desc: "Add your #1 most relevant sample here",
      },
      {
        icon: "📁",
        tag: "Sample",
        name: "Second Best Sample",
        desc: "Add your second most relevant sample here",
      },
      {
        icon: "🔗",
        tag: "Profile",
        name: "Full Portfolio",
        desc: "Link to Upwork profile or portfolio site",
      },
    ],
    ctaTitle: (
      <>
        I'm ready to
        <br />
        start <em>quickly.</em>
      </>
    ),
    ctaBody:
      "No long back-and-forth. Let's take 15 minutes to confirm the brief and I'll give you a clear timeline and first step. That's it.",
  },
};

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;0,900;1,400;1,700&family=DM+Sans:wght@300;400;500;600&family=DM+Mono:wght@400;500&display=swap');

:root {
  --navy: #0B1D3A;
  --navy-mid: #122952;
  --navy-light: #1A3A6B;
  --gold: #C9A84C;
  --gold-light: #E8C96A;
  --cream: #FAF6ED;
  --white: #FFFFFF;
  --text-dark: #0B1D3A;
  --text-mid: #3A4F72;
  --text-light: #6B82A8;
}

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
html { scroll-behavior: smooth; }

.mt-body { font-family: 'DM Sans', sans-serif; background: var(--cream); color: var(--text-dark); overflow-x: hidden; }

/* ── HERO ── */
.mt-hero {
  position: relative; min-height: 100vh; background: var(--navy);
  display: flex; flex-direction: column; justify-content: center; align-items: center;
  padding: 80px 40px; overflow: hidden;
}
.mt-hero::before {
  content: ''; position: absolute; inset: 0;
  background: radial-gradient(ellipse 70% 50% at 20% 50%, rgba(201,168,76,0.12) 0%, transparent 60%),
              radial-gradient(ellipse 50% 70% at 80% 30%, rgba(26,58,107,0.6) 0%, transparent 60%);
}
.mt-hero-grid {
  position: absolute; inset: 0;
  background-image: linear-gradient(rgba(201,168,76,0.04) 1px, transparent 1px),
                    linear-gradient(90deg, rgba(201,168,76,0.04) 1px, transparent 1px);
  background-size: 60px 60px; pointer-events: none;
}

.mt-hero-ornament {
  position: absolute; top: -120px; right: -120px; width: 500px; height: 500px;
  border-radius: 50%; border: 1px solid rgba(201,168,76,0.15); pointer-events: none;
}
.mt-hero-ornament::before { content:''; position:absolute; inset:40px; border-radius:50%; border:1px solid rgba(201,168,76,0.1); }
.mt-hero-ornament::after  { content:''; position:absolute; inset:80px; border-radius:50%; border:1px solid rgba(201,168,76,0.07); }

.mt-badge {
  position: relative; display: inline-flex; align-items: center; gap: 8px;
  background: rgba(201,168,76,0.15); border: 1px solid rgba(201,168,76,0.4);
  border-radius: 100px; padding: 8px 20px; margin-bottom: 32px;
  animation: mt-fadeUp 0.8s ease both;
}
.mt-badge span { font-family: 'DM Mono', monospace; font-size: 11px; letter-spacing: 0.15em; text-transform: uppercase; color: var(--gold-light);  white-space: nowrap;}
.mt-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--gold); animation: mt-pulse 2s ease infinite; }
@keyframes mt-pulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(.8)} }

.mt-eyebrow { position:relative; font-family:'DM Mono',monospace; font-size:11px; letter-spacing:.2em; text-transform:uppercase; color:var(--gold); margin-bottom:16px; animation:mt-fadeUp .8s .1s ease both; }
.mt-hero-title { position:relative; font-family:'Playfair Display',serif; font-size:clamp(42px,7vw,80px); font-weight:900; color:var(--white); text-align:center; line-height:1.08; margin-bottom:8px; animation:mt-fadeUp .9s .2s ease both; }
.mt-hero-title em { font-style:italic; color:var(--gold-light); }
.mt-hero-sub { position:relative; font-size:17px; font-weight:300; color:rgba(255,255,255,.55); text-align:center; max-width:520px; line-height:1.7; margin-top:20px; animation:mt-fadeUp .9s .35s ease both; }

.mt-creds {
  display: flex;
  gap: 12px;
  flex-wrap: nowrap;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  margin-top:20px;
}

.mt-creds::-webkit-scrollbar {
  display: none;
}
.mt-cred-pill { display:flex; align-items:center; gap:10px; background:rgba(255,255,255,.05); border:1px solid rgba(255,255,255,.12); border-radius:12px; padding:12px 20px; backdrop-filter:blur(10px); }
.mt-cred-pill .cp-label { font-size:13px; font-weight:600; color:var(--white); }
.mt-cred-pill .cp-sub { font-size:11px; color:rgba(255,255,255,.45); }
@media (max-width: 768px) {
  .mt-cred-pill {
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 6px;
  }
}
.mt-scroll { position:absolute; bottom:36px; left:50%; transform:translateX(-50%); display:flex; flex-direction:column; align-items:center; gap:8px; animation:mt-fadeIn 1s 1.2s ease both; }
.mt-scroll span { font-size:10px; letter-spacing:.15em; text-transform:uppercase; color:rgba(255,255,255,.3); }
.mt-scroll-line { width:1px; height:40px; background:linear-gradient(to bottom,rgba(201,168,76,.6),transparent); animation:mt-scrollAnim 1.5s ease infinite; }
@keyframes mt-scrollAnim { 0%{transform:scaleY(0);transform-origin:top} 50%{transform:scaleY(1);transform-origin:top} 51%{transform:scaleY(1);transform-origin:bottom} 100%{transform:scaleY(0);transform-origin:bottom} }
@keyframes mt-fadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
@keyframes mt-fadeIn { from{opacity:0} to{opacity:1} }

/* ── STORY ── */
.mt-story-wrap { background:var(--navy); position:relative; overflow:hidden; }
.mt-story-wrap::before { content:''; position:absolute; top:0; left:0; right:0; bottom:0; background:var(--cream); clip-path:polygon(55% 0,100% 0,100% 100%,45% 100%); z-index:0; }
.mt-story-inner { max-width:1200px; margin:0 auto; display:grid; grid-template-columns:1fr 1.1fr; min-height:100vh; position:relative; z-index:1; }

.mt-story-left { background:var(--navy); padding:10px 30px 60px 20px; display:flex; flex-direction:column; justify-content:center; position:relative; }
.mt-story-left::after { content:''; position:absolute; top:0; right:-1px; bottom:0; width:2px; background:linear-gradient(to bottom,transparent,var(--gold),transparent); }

.mt-chapter-label { font-family:'DM Mono',monospace; font-size:9px; letter-spacing:.3em; text-transform:uppercase; color:var(--gold); margin-bottom:32px; display:flex; align-items:center; gap:12px; }
.mt-chapter-label::before { content:''; width:32px; height:1px; background:var(--gold); opacity:.5; }

.mt-story-headline { font-family:'Playfair Display',serif; font-size:clamp(30px,3.2vw,44px); font-weight:900; color:var(--white); line-height:1.12; margin-bottom:40px; }
.mt-story-headline em { font-style:italic; color:var(--gold-light); }

/* Timeline */
.mt-timeline { display:flex; flex-direction:column; position:relative; }
.mt-timeline::before { content:''; position:absolute; left:15px; top:6px; bottom:6px; width:1px; background:linear-gradient(to bottom,var(--gold),rgba(201,168,76,.1)); }

.mt-tl-entry { display:flex; gap:24px; padding-bottom:36px; position:relative; }
.mt-tl-entry:last-child { padding-bottom:0; }
.mt-tl-dot { flex-shrink:0; width:30px; height:30px; border-radius:50%; background:var(--navy-mid); border:1.5px solid var(--gold); display:flex; align-items:center; justify-content:center; position:relative; z-index:1; margin-top:2px; transition:all .3s ease; font-size:13px; }
.mt-tl-entry:hover .mt-tl-dot { background:var(--gold); box-shadow:0 0 16px rgba(201,168,76,.5); transform:scale(1.15); }
.mt-tl-year { font-family:'DM Mono',monospace; font-size:9px; letter-spacing:.2em; color:var(--gold); opacity:.7; text-transform:uppercase; margin-bottom:4px; }
.mt-tl-title { font-family:'Playfair Display',serif; font-size:16px; font-weight:700; color:var(--white); margin-bottom:6px; }
.mt-tl-desc { font-size:13px; color:rgba(255,255,255,.45); line-height:1.65; }

/* Story Right */
.mt-story-right { background:transparent; padding:80px 30px 80px 30px; display:flex; flex-direction:column; justify-content:center; }
.mt-chapter-pill { display:inline-flex; align-items:center; gap:8px; background:rgba(201,168,76,.12); border:1px solid rgba(201,168,76,.35); border-radius:100px; padding:6px 16px; margin-bottom:20px; }
.mt-chapter-pill span { font-family:'DM Mono',monospace; font-size:10px; letter-spacing:.15em; text-transform:uppercase; color:var(--gold); }

.mt-pullquote { background:var(--navy); border-radius:16px; padding:28px 32px; margin-bottom:36px; position:relative; overflow:hidden; }
.mt-pullquote::before { content:'\\201C'; font-family:'Playfair Display',serif; font-size:100px; line-height:1; color:var(--gold); opacity:.15; position:absolute; top:0; left:16px; }
.mt-pullquote::after { content:''; position:absolute; bottom:0; left:0; right:0; height:3px; background:linear-gradient(to right,var(--gold),transparent); }
.mt-pq-text { font-family:'Playfair Display',serif; font-size:19px; font-style:italic; color:var(--white); line-height:1.55; position:relative; z-index:1; margin-bottom:12px; }
.mt-pq-attr { font-size:11px; letter-spacing:.12em; text-transform:uppercase; color:var(--gold); font-family:'DM Mono',monospace; }

.mt-story-body { font-size:15px; line-height:1.9; color:var(--text-mid); margin-bottom:16px; }
.mt-story-body strong { color:var(--navy); font-weight:600; }

.mt-cred-strip { display:flex; gap:12px; margin-top:28px; flex-wrap:wrap; }
.mt-cred-chip { display:flex; align-items:center; gap:7px; background:var(--white); border:1px solid rgba(11,29,58,.1); border-radius:8px; padding:8px 14px; box-shadow:0 2px 8px rgba(11,29,58,.05); }
.mt-cred-chip .cc-icon { font-size:16px; }
.mt-cred-chip .cc-text { font-size:12px; font-weight:600; color:var(--navy); }

.mt-placeholder { background:rgba(201,168,76,.08); border:1px dashed rgba(201,168,76,.4); border-radius:8px; padding:14px 18px; font-size:13px; color:var(--gold); font-style:italic; margin-bottom:12px; display:flex; align-items:center; gap:10px; }
.mt-placeholder::before { content:'✏'; font-style:normal; font-size:14px; }

/* ── PROCESS ── */
.mt-process-wrap { background:var(--navy); padding:100px 0 0; position:relative; overflow:hidden; }
.mt-process-wrap::before { content:''; position:absolute; inset:0; background-image:url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.03'/%3E%3C/svg%3E"); opacity:.4; pointer-events:none; }
.mt-process-inner { max-width:1100px; margin:0 auto; padding:0 20px; position:relative; z-index:1; }
.mt-process-header { margin-bottom:64px; }
.mt-process-sub { font-size:15px; color:rgba(255,255,255,.4); max-width:500px; line-height:1.7; margin-top:10px; }

.mt-section-label { font-family:'DM Mono',monospace; font-size:10px; letter-spacing:.25em; text-transform:uppercase; color:var(--gold); margin-bottom:12px; display:flex; align-items:center; gap:12px; }
.mt-section-label::after { content:''; flex:1; max-width:60px; height:1px; background:var(--gold); opacity:.4; }
.mt-section-title { font-family:'Playfair Display',serif; font-size:clamp(32px,4vw,52px); font-weight:700; color:var(--navy); line-height:1.15; margin-bottom:16px; }
.mt-section-title em { font-style:italic; color:var(--gold); }
.mt-section-title-white { color:var(--white); }

/* Flow Track */
.mt-flow-track { position:relative; display:grid; grid-template-columns:repeat(4,1fr); gap:0; }
.mt-flow-track::before { content:''; position:absolute; top:52px; left:12.5%; right:12.5%; height:1px; background:linear-gradient(to right,transparent,rgba(201,168,76,.6) 10%,rgba(201,168,76,.6) 90%,transparent); z-index:0; }
.mt-flow-track::after { content:''; position:absolute; top:49px; left:12.5%; width:60px; height:7px; border-radius:4px; background:linear-gradient(to right,transparent,rgba(201,168,76,.8),transparent); filter:blur(2px); animation:mt-flowPulse 3s ease-in-out infinite; z-index:1; }
@keyframes mt-flowPulse { 0%{left:12.5%;opacity:0} 10%{opacity:1} 90%{opacity:1} 100%{left:calc(87.5% - 60px);opacity:0} }

.mt-flow-step { display:flex; flex-direction:column; align-items:center; text-align:center; padding:0 20px; position:relative; z-index:2; cursor:default; }
.mt-flow-node { position:relative; width:104px; height:104px; border-radius:50%; background:var(--navy-mid); border:1px solid rgba(201,168,76,.25); display:flex; flex-direction:column; align-items:center; justify-content:center; margin-bottom:28px; transition:all .4s cubic-bezier(.34,1.56,.64,1); }
.mt-flow-node::before { content:''; position:absolute; inset:-8px; border-radius:50%; border:1px solid rgba(201,168,76,.12); transition:all .4s ease; }
.mt-flow-node::after { content:''; position:absolute; inset:12px; border-radius:50%; background:radial-gradient(circle,rgba(201,168,76,.06) 0%,transparent 70%); transition:all .4s ease; }

.mt-flow-step:hover .mt-flow-node,
.mt-flow-step.active .mt-flow-node { border-color:var(--gold); background:linear-gradient(135deg,var(--navy-mid) 0%,rgba(201,168,76,.15) 100%); transform:translateY(-8px); box-shadow:0 20px 60px rgba(11,29,58,.5),0 0 0 1px rgba(201,168,76,.3),0 0 40px rgba(201,168,76,.15); }
.mt-flow-step:hover .mt-flow-node::before { inset:-14px; border-color:rgba(201,168,76,.2); }

.mt-flow-icon { font-size:28px; line-height:1; margin-bottom:4px; position:relative; z-index:1; }
.mt-flow-num { font-family:'DM Mono',monospace; font-size:10px; letter-spacing:.1em; color:rgba(201,168,76,.6); position:relative; z-index:1; }
.mt-flow-title { font-family:'Playfair Display',serif; font-size:17px; font-weight:700; color:var(--white); margin-bottom:10px; transition:color .3s; }
.mt-flow-step:hover .mt-flow-title, .mt-flow-step.active .mt-flow-title { color:var(--gold-light); }
.mt-flow-desc { font-size:12.5px; color:rgba(255,255,255,.38); line-height:1.65; margin:0 auto; transition:color .3s; }
.mt-flow-step:hover .mt-flow-desc, .mt-flow-step.active .mt-flow-desc { color:rgba(255,255,255,.65); }

/* Detail bar */
.mt-detail-bar { margin-top:56px; padding:0 40px; background:rgba(201,168,76,.06); border-top:1px solid rgba(201,168,76,.15); }
.mt-detail-inner { max-width:1100px; margin:0 auto; padding:36px 0; display:grid; grid-template-columns:auto 1fr auto; align-items:center; gap:32px; min-height:100px; }
.mt-fdb-num { font-family:'Playfair Display',serif; font-size:64px; font-weight:900; color:rgba(201,168,76,.15); line-height:1; letter-spacing:-2px; transition:all .4s ease; }
.mt-fdb-title { font-family:'Playfair Display',serif; font-size:20px; font-weight:700; color:var(--white); margin-bottom:6px; transition:all .3s ease; }
.mt-fdb-desc { font-size:13.5px; color:rgba(255,255,255,.5); line-height:1.7; max-width:600px; transition:all .3s ease; }
.mt-fdb-tag { font-family:'DM Mono',monospace; font-size:10px; letter-spacing:.18em; text-transform:uppercase; color:var(--gold); border:1px solid rgba(201,168,76,.3); border-radius:100px; padding:6px 16px; white-space:nowrap; transition:all .3s ease; }

/* ── OFFER ── */
.mt-offer-wrap { background:var(--cream); padding:100px 40px; }
.mt-offer-inner { max-width:1100px; margin:0 auto; }
.mt-offer-grid { display:grid; grid-template-columns:1.2fr 1fr; gap:60px; align-items:start; margin-top:48px; }
.mt-offer-body { font-size:16px; line-height:1.85; color:var(--text-mid); margin-bottom:20px; }

.mt-proof-card { background:var(--white); border-radius:16px; padding:28px; position:relative; box-shadow:0 4px 20px rgba(11,29,58,.06); border:1px solid rgba(201,168,76,.15); margin-bottom:20px; transition:transform .2s,box-shadow .2s; }
.mt-proof-card:hover { transform:translateY(-3px); box-shadow:0 10px 36px rgba(11,29,58,.1); }
.mt-proof-card::before { content:'\\201C'; font-family:'Playfair Display',serif; font-size:72px; color:var(--gold); opacity:.2; position:absolute; top:8px; left:20px; line-height:1; }
.mt-stars { color:var(--gold); font-size:14px; margin-bottom:10px; }
.mt-proof-quote { font-family:'Playfair Display',serif; font-size:17px; font-style:italic; color:var(--navy); line-height:1.55; margin-bottom:14px; padding-top:12px; position:relative; z-index:1; }
.mt-proof-attr { font-size:12px; font-weight:600; color:var(--gold); letter-spacing:.08em; text-transform:uppercase; }

.mt-stat-row { display:grid; grid-template-columns:repeat(3,1fr); gap:16px; margin-top:32px; }
@media (max-width: 768px) {
  .mt-stat-card {
    height: 100px;
  }
}
  .mt-stat-num {
  font-size: 32px;
  line-height: 1;
  margin-bottom: 6px;
}
.mt-stat-card { background:var(--navy); border-radius:12px; padding:24px 6px; text-align:center; max-width:120px; }
.mt-stat-num { font-family:'Playfair Display',serif; font-size:34px; font-weight:900; color:var(--gold); line-height:1; }
.mt-stat-label { font-size:11px; color:rgba(255,255,255,.5); margin-top:6px; text-transform:uppercase; letter-spacing:.1em; }

/* Tools */
.mt-tools-row { display:flex; gap:16px; flex-wrap:wrap; margin-top:40px; }
.mt-tool-badge { display:flex; align-items:center; gap:8px; background:var(--white); border:1px solid rgba(11,29,58,.1); border-radius:8px; padding:10px 18px; transition:border-color .2s; }
.mt-tool-badge:hover { border-color:var(--gold); }
.mt-tool-badge .t-icon { font-size:18px; }
.mt-tool-badge .t-name { font-size:13px; font-weight:600; color:var(--navy); }

/* ── PORTFOLIO ── */
.mt-portfolio-wrap { background:var(--white); padding:100px 40px; }
.mt-portfolio-inner { max-width:1100px; margin:0 auto; }
.mt-portfolio-grid { display:grid; grid-template-columns:repeat(3,1fr); gap:24px; margin-top:48px; }
@media (max-width: 768px) {
  .mt-portfolio-grid {
  grid-template-columns:repeat(1fr)
  }
}
@media (max-width: 768px) {
  .mt-portfolio-grid {
    grid-template-columns: 1fr !important;
    gap: 20px;
  }

  .mt-port-card {
    max-width: 100%;
  }
}
.mt-port-card { border-radius:12px; overflow:hidden; background:var(--cream); border:1px solid rgba(11,29,58,.08); transition:transform .3s,box-shadow .3s; cursor:pointer; }
.mt-port-card:hover { transform:translateY(-6px); box-shadow:0 16px 48px rgba(11,29,58,.12); }
.mt-port-thumb { aspect-ratio:16/10; background:linear-gradient(135deg,var(--navy) 0%,var(--navy-light) 100%); display:flex; align-items:center; justify-content:center; position:relative; overflow:hidden; }
.mt-port-thumb::after { content:''; position:absolute; inset:0; background:radial-gradient(ellipse at 30% 70%,rgba(201,168,76,.15) 0%,transparent 60%); }
.mt-port-icon { font-size:40px; position:relative; z-index:1; opacity:.6; }
.mt-port-tag { position:absolute; top:12px; left:12px; background:var(--gold); color:var(--navy); font-size:9px; font-weight:700; letter-spacing:.1em; text-transform:uppercase; padding:4px 10px; border-radius:4px; z-index:2; }
.mt-port-info { padding:18px 20px; }
.mt-port-name { font-family:'Playfair Display',serif; font-size:16px; font-weight:700; color:var(--navy); margin-bottom:6px; }
.mt-port-desc { font-size:13px; color:var(--text-light); line-height:1.6; }
.mt-port-note { font-size:11px; color:var(--gold); font-style:italic; margin-top:8px; }

/* ── CTA ── */
.mt-cta-wrap { background:linear-gradient(135deg,var(--navy) 0%,var(--navy-light) 100%); padding:100px 40px; position:relative; overflow:hidden; text-align:center; }
.mt-cta-wrap::before { content:''; position:absolute; inset:0; background:radial-gradient(ellipse 60% 80% at 50% 50%,rgba(201,168,76,.08) 0%,transparent 70%); pointer-events:none; }
.mt-cta-inner { max-width:700px; margin:0 auto; position:relative; z-index:1; }
.mt-cta-title { font-family:'Playfair Display',serif; font-size:clamp(32px,4vw,52px); font-weight:700; color:var(--white); line-height:1.2; margin-bottom:20px; }
.mt-cta-title em { font-style:italic; color:var(--gold-light); }
.mt-cta-body { font-size:16px; color:rgba(255,255,255,.55); line-height:1.8; margin-bottom:40px; }
.mt-cta-sig { font-family:'Playfair Display',serif; font-size:28px; font-style:italic; color:var(--gold-light); margin-top:48px; }
.mt-cta-sig-sub { font-size:12px; letter-spacing:.15em; text-transform:uppercase; color:rgba(255,255,255,.35); margin-top:6px; }

/* ── TEMPLATE SELECTOR ── */
.mt-tpl-selector { position:fixed; top:24px; right:24px; z-index:1000; background:rgba(11,29,58,.95); border:1px solid rgba(201,168,76,.3); border-radius:12px; padding:16px; backdrop-filter:blur(12px); box-shadow:0 8px 32px rgba(0,0,0,.3); }
.mt-ts-label { font-size:9px; letter-spacing:.15em; text-transform:uppercase; color:rgba(255,255,255,.4); margin-bottom:10px; font-family:'DM Mono',monospace; }
.mt-ts-btn { background:transparent; border:1px solid rgba(201,168,76,.2); color:rgba(255,255,255,.6); font-size:11px; font-family:'DM Sans',sans-serif; font-weight:500; padding:7px 14px; border-radius:6px; cursor:pointer; transition:all .2s; text-align:left; display:block; width:100%; margin-bottom:6px; }
.mt-ts-btn:last-child { margin-bottom:0; }
.mt-ts-btn:hover, .mt-ts-btn.active { background:rgba(201,168,76,.15); border-color:var(--gold); color:var(--gold); }

@media (max-width:768px) {
  .mt-story-inner { grid-template-columns:1fr; }
  .mt-story-wrap::before { display:none; }
  .mt-story-right { background:var(--cream); }
  .mt-flow-track { grid-template-columns:repeat(2,1fr); gap:40px; }
  .mt-flow-track::before, .mt-flow-track::after { display:none; }
  .mt-offer-grid { grid-template-columns:1fr; }
  .mt-portfolio-grid { grid-template-columns:repeat(2,1fr); }
  .mt-tpl-selector { display:none; }
  .mt-detail-inner { grid-template-columns:1fr; }
}
  @media (max-width: 768px) {
  .mt-flow-track {
    grid-template-columns: 1fr;   /* 🔥 ek row me ek */
    gap: 30px;
  }
}
`;

type Proposal = {
  fullName?: string;
  title?: string;
  template?: string;
  sections?: any[];
  [key: string]: any;
};

const ModernTemplate = ({ proposal }: { proposal?: Proposal }) => {
const [tpl, setTpl] = useState<TemplateKey>("design");
const [activeStep, setActiveStep] = useState<number | null>(null);

  useEffect(() => {
    const id = "mt-modern-css";
    if (!document.getElementById(id)) {
      const s = document.createElement("style");
      s.id = id;
      s.innerHTML = CSS;
      document.head.appendChild(s);
    }
    return () => document.getElementById(id)?.remove();
  }, []);

  const cfg = TEMPLATES[tpl];
  const fullName = proposal?.fullName || "Irfan Baig";
  const firstName = fullName.split(" ")[0];
  const lastName = fullName.split(" ").slice(1).join(" ");

  const defaultDetail = {
    num: "—",
    title: "Hover a step above to explore the process",
    desc: "Each stage is designed to keep you informed, in control, and confident about the outcome.",
    tag: "Process",
  };

const step = activeStep !== null ? cfg.steps[activeStep] : undefined;

const detail = step
  ? {
      num: `0${activeStep! + 1}`,
      title: step.title,
      desc: step.desc,
      tag: step.tag,
    }
  : defaultDetail;
const TEMPLATE_OPTIONS = [
  { key: "design", label: "🎨 Design & Multimedia" },
  { key: "it", label: "💻 IT & Programming" },
  { key: "short", label: "⚡ Short-Form Punchy" },
] as const;
  return (
    <div className="mt-body">

      <section className="mt-hero">
        <div className="mt-hero-grid" />
        <div className="mt-hero-ornament" />
        <div className="mt-badge">
          <span className="mt-dot" />
          <span>Upwork · Top Rated · 100% Job Success</span>
        </div>
        <p className="mt-eyebrow">A Proposal By</p>
        <h1 className="mt-hero-title">
          {firstName} <em>{lastName}</em>
        </h1>
        <p className="mt-hero-sub">{cfg.heroSub}</p>
        <div className="mt-creds">
          <div className="mt-cred-pill">
            <span style={{ fontSize: 20 }}>⭐</span>
            <div>
              <div className="cp-label">Top Rated</div>
              <div className="cp-sub">Upwork Status</div>
            </div>
          </div>
          <div className="mt-cred-pill">
            <span style={{ fontSize: 20 }}>✅</span>
            <div>
              <div className="cp-label">100% JSS</div>
              <div className="cp-sub">Job Success Score</div>
            </div>
          </div>
          <div className="mt-cred-pill">
            <span style={{ fontSize: 20 }}>{cfg.heroSkillIcon}</span>
            <div>
              <div className="cp-label">{cfg.heroSkill}</div>
              <div className="cp-sub">{cfg.heroSkillSub}</div>
            </div>
          </div>
        </div>
        <div className="mt-scroll">
          <div className="mt-scroll-line" />
          <span>Scroll to explore</span>
        </div>
      </section>

      <div style={{ background: "#f7f6f2", padding: "72px 20px" }}>
        <div style={{ maxWidth: 1080, margin: "0 auto" }}>
          <div className="space-y-7">
            {(proposal?.sections || [])
              .slice()
              .filter((s: any) => !s.hidden)
              .sort((a: any, b: any) => (a.order || 0) - (b.order || 0))
              .map((section: any, index: number) => {
                const card = "bg-white rounded-2xl border border-[#e7e2d6] shadow-[0_8px_30px_rgba(20,40,20,0.06)] p-8 sm:p-10";
                const titleRow = (t: string) => (
                  <div className="flex items-center gap-3 mb-6">
                    <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#c9a84c]" />
                    <h2 className="text-[22px] sm:text-[26px] font-extrabold text-[#0c2a17]">{t}</h2>
                  </div>
                );

                if (section.type === "simple-text") {
                  return (
                    <div key={index} className={card}>
                      {index === 0 && (
                        <div className="text-center mb-8">
                          <h1 className="text-[26px] sm:text-[34px] font-black text-[#0c2a17]">{proposal?.title || "Project Proposal"}</h1>
                          <div className="w-16 h-1.5 bg-[#c9a84c] rounded-full mx-auto mt-3" />
                        </div>
                      )}
                      {titleRow(section.title)}
                      <p className="text-[#3f5142] leading-relaxed text-[16px] whitespace-pre-line">{section.content?.text}</p>
                    </div>
                  );
                }
                if (section.type === "feature-grid") {
                  return (
                    <div key={index} className={card}>
                      {titleRow(section.title)}
                      <div className="grid sm:grid-cols-2 gap-5">
                        {(section.content?.items || []).map((it: any, i: number) => (
                          <div key={i} className="rounded-xl bg-[#faf8f2] border border-[#ece6d6] p-5">
                            <h3 className="font-bold text-[#0c2a17]">{it.title}</h3>
                            <p className="text-[#5b6b5c] text-sm mt-1.5">{it.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                if (section.type === "dark-grid") {
                  return (
                    <div key={index} className="rounded-2xl p-8 sm:p-10 bg-[#0c2a17] text-white shadow-[0_8px_30px_rgba(20,40,20,0.18)]">
                      <div className="flex items-center gap-3 mb-6">
                        <span className="inline-block w-2.5 h-2.5 rounded-full bg-[#c9a84c]" />
                        <h2 className="text-[22px] sm:text-[26px] font-extrabold">{section.title}</h2>
                      </div>
                      <div className="grid sm:grid-cols-3 gap-5">
                        {(section.content?.items || []).map((it: any, i: number) => (
                          <div key={i} className="rounded-xl bg-white/5 border border-white/10 p-5">
                            <h3 className="text-[#d8c074] font-bold">{it.title}</h3>
                            <p className="text-white/75 text-sm mt-1.5">{it.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                if (section.type === "steps") {
                  return (
                    <div key={index} className={card}>
                      {titleRow(section.title)}
                      <div className="grid sm:grid-cols-3 gap-5">
                        {(section.content?.steps || []).map((st: any, i: number) => (
                          <div key={i} className="rounded-xl bg-[#faf8f2] border border-[#ece6d6] p-5">
                            <div className="w-9 h-9 rounded-full bg-[#0c2a17] text-[#c9a84c] grid place-items-center font-bold mb-3">{i + 1}</div>
                            <h3 className="font-bold text-[#0c2a17]">{st.title}</h3>
                            <p className="text-[#5b6b5c] text-sm mt-1.5 whitespace-pre-line">{st.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                if (section.type === "text-with-points") {
                  return (
                    <div key={index} className={card}>
                      {titleRow(section.title)}
                      <div className="grid md:grid-cols-2 gap-8 items-start">
                        <div>
                          <p className="text-[#3f5142] leading-relaxed mb-5">{section.content?.text}</p>
                          <div className="space-y-3">
                            {(section.content?.points || []).map((pt: any, i: number) => (
                              <div key={i} className="flex items-start gap-2.5">
                                <span className="text-[#c9a84c] mt-0.5 font-bold">+</span>
                                <p className="text-sm text-[#374a39]">{pt}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                        {section.content?.highlight && (
                          <div className="rounded-2xl border border-dashed border-[#c9a84c]/50 bg-[#faf8f2] p-8 text-center">
                            <p className="text-[#0c2a17] font-semibold text-lg leading-relaxed whitespace-pre-line">{section.content.highlight}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }
                if (section.type === "testimonial-grid") {
                  return (
                    <div key={index} className={card}>
                      {titleRow(section.title)}
                      <div className="grid sm:grid-cols-3 gap-5">
                        {(section.content?.items || []).map((it: any, i: number) => {
                          const initials = (it.name || "")
                            .split(" ").map((n: string) => n[0]).join("").substring(0, 2).toUpperCase();
                          return (
                            <div key={i} className="relative rounded-xl bg-[#faf8f2] border border-[#ece6d6] p-6">
                              <p className="text-sm text-[#5b6b5c] leading-relaxed mb-6">&quot;{it.quote}&quot;</p>
                              <div className="flex items-center gap-3">
                                <div className="w-11 h-11 rounded-full bg-[#0c2a17] text-[#c9a84c] grid place-items-center text-sm font-bold">{initials}</div>
                                <div>
                                  <p className="font-semibold text-[#0c2a17] text-sm">{it.name}</p>
                                  <p className="text-xs text-[#7a857b]">{it.position}</p>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                if (section.type === "website-showcase") {
                  return <WebsiteShowcaseSection key={section.id} section={section} primaryColor={proposal.customization?.primaryColor} />;
                }
                if (section.type === "portfolio-grid") {
                  return (
                    <div key={index} className={card}>
                      <div className="text-center mb-8">
                        <h2 className="text-[24px] sm:text-[28px] font-extrabold text-[#0c2a17]">{section.title}</h2>
                        <div className="w-16 h-1.5 bg-[#c9a84c] rounded-full mx-auto mt-3" />
                        {section.content?.subtitle && <p className="text-[#6b766c] mt-3">{section.content.subtitle}</p>}
                      </div>
                      <div className="grid sm:grid-cols-3 gap-5">
                        {(section.content?.projects || []).map((pr: any, i: number) => (
                          <div key={i} className="rounded-xl overflow-hidden border border-[#ece6d6] bg-white">
                            {pr.image
                              ? <img src={pr.image} alt={pr.title || "project"} className="w-full h-44 object-contain bg-slate-100" />
                              : <div className="w-full h-44 bg-[#0c2a17] grid place-items-center text-[#c9a84c] text-3xl font-bold">*</div>}
                            <div className="p-4">
                              <p className="font-semibold text-sm text-[#0c2a17]">{pr.title}</p>
                              {pr.description && <p className="text-xs text-[#7a857b] mt-1">{pr.description}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                }
                if (section.type === "call-to-action") {
                  return (
                    <div key={index} className="rounded-2xl p-12 text-center bg-[#0c2a17] text-white">
                      <h2 className="text-[24px] sm:text-[30px] font-black mb-4">{section.title}</h2>
                      {section.content?.text && <p className="text-white/80 max-w-2xl mx-auto mb-7">{section.content.text}</p>}
                      <a href={section.content?.buttonLink || proposal.hireLink || BRAND_CONFIGS[proposal.brand as BrandType]?.upworkProfile || "#"} target="_blank" rel="noopener noreferrer" className="inline-block bg-[#c9a84c] text-[#0c2a17] font-bold px-7 py-3 rounded-full hover:brightness-105 transition">{section.content?.buttonText || ("Work with " + (proposal?.fullName || "me"))}</a>
                    </div>
                  );
                }
                return (
                  <div key={index} className={card}>
                    {titleRow(section.title || "Section")}
                    <p className="text-[#3f5142] whitespace-pre-line">{typeof section.content === "string" ? section.content : section.content?.text}</p>
                  </div>
                );
              })}
          </div>
        </div>
      </div>

      <div className="mt-cta-wrap">
        <div className="mt-cta-inner">
          <h2 className="mt-cta-title">Let&apos;s build something <em>exceptional</em></h2>
          <p className="mt-cta-body">If what you&apos;ve read resonates, I&apos;d love a quick call to understand your project before we discuss anything else.</p>
          <div className="mt-cta-sig">{fullName}</div>
          <div className="mt-cta-sig-sub">Top Rated - 100% Job Success - Upwork</div>
        </div>
      </div>
    </div>
  );
};

export default ModernTemplate;
